<template>
  <v-dialog
    :value="opened"
    width="600"
    persistent
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.esc="cancel()"
  >
    <v-card block flat>
      <v-form v-model="isFormValid">
        <v-card-title>
          <sc-h3>Send Tax Return to Documents</sc-h3>
        </v-card-title>

        <v-card-text class="text-xs-left">
          <b>Subject</b>
          <v-text-field
            class="default-input mt-1 pb-3"
            v-model="subject"
            solo
            hide-details
          />

          <v-checkbox
            class="mt-0"
            v-model="saveSubjectForFutureUse"
            label="Save for future use"
          />

          <v-layout align-center>
            <b>Message</b>
          </v-layout>
          <quill-editor
            v-model="message"
            :options="editorOption"
            class="message-input mt-2 pb-3 default-input"
          />
          <v-checkbox
            class="mt-0"
            v-model="saveMessageForFutureUse"
            label="Save for future use"
          />

          <v-layout mt-1>
            <b>Path: {{ path }}</b>
          </v-layout>

          <v-layout mt-3>
            <v-flex xs-12>
              <b>Document Name</b>
              <v-text-field
                class="default-input mt-1 pb-3"
                v-model="documentName"
                solo
                hide-details
                :rules="[endsWithPdf]"
              />
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            flat
            outline
            class="grey text-none font-weight-bold"
            :disabled="sending"
            @click="cancel()"
          >
            Cancel
          </v-btn>

          <v-btn
            class="text-none font-weight-bold"
            color="primary"
            @click="send()"
            :disabled="!isFormValid"
            :loading="sending"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  mixins: [FirmPreferenceMixins],

  props: {
    opened: {
      type: Boolean,
    },

    taxReturn: {
      type: Object,
    },
  },

  data() {
    return {
      subject: '',
      message: '',
      documentName: this.taxReturn.tax_return_name,
      saveSubjectForFutureUse: false,
      saveMessageForFutureUse: false,
      sending: false,

      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
          ],
        },
        placeholder: 'Message',
        bounds: '.message-input',
      },

      isFormValid: false,

      endsWithPdf: value => {
        return (
          value.endsWith('.pdf') ||
          value.endsWith('.PDF') ||
          'Document name must end with .pdf'
        );
      },
    };
  },

  methods: {
    cancel() {
      this.$emit('update:opened', false);
    },

    async send() {
      try {
        this.sending = true;

        const response = await API.taxReturn.sendToDocuments({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
          subject: this.subject,
          message: this.message,
          documentName: this.documentName,
          path: this.path,
          saveSubjectForFutureUse: this.saveSubjectForFutureUse,
          saveMessageForFutureUse: this.saveMessageForFutureUse,
        });

        this.$emit('update:opened', false);

        // If save subject or message for future use is checked, the firm preferences will be updated.
        // Thus, we need to refresh the firm to use the updated values.
        if (this.saveSubjectForFutureUse || this.saveMessageForFutureUse) {
          this.$store.dispatch('setCurrentFirm');
        }

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Tax return has been sent to Documents successfully.',
        });
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Failed to send tax return to Documents.',
          errorToSend: error,
        });
      } finally {
        this.sending = false;
      }
    },
  },

  computed: {
    path() {
      if (this.taxReturnSendToDocumentsPath) {
        return this.taxReturnSendToDocumentsPath.replace(
          '{year}',
          this.taxReturn.tax_year,
        );
      } else {
        return '/' + this.taxReturn.tax_year + '/Tax Return';
      }
    },
  },

  async created() {
    this.subject =
      this.sendTaxReturnToDocumentsDefaultSubject ||
      'Your tax return is now accessible in Documents Portal.';
    this.message =
      this.sendTaxReturnToDocumentsDefaultMessage ||
      "Hello {client_preferred_name},<br/><br/>We're pleased to inform you that your tax return is now available for access through the Documents Portal. Please click the button below to view and download your tax return.<br/><br/>Best regards";
  },
};
</script>

<style scoped></style>
